import type { FlatSlot } from '../../../atoms/slot';
import type { TranscriptionColor } from '../../../colors/colors';
import type { OutputAnnotation } from '../../../types/annotation';
import type { StructuredText } from '../../../types/annotation';
import { ANNOTATION_LABEL_TYPE, INLINE_TAG_TYPE } from '../../../types/annotation-label';
import { getLabelDisplayName } from '../../../utils/label-display';
import { getDeduplicatedGlobalTextForWer } from './get-deduplicated-global-text';
const PLACEHOLDER_COLOR = ({
  selected: '#000',
  unselected: '#000'
} as TranscriptionColor);
const normalizeSpeakerTagForWer = (labelDisplayName: string): string => labelDisplayName.toLowerCase().replace(/\s+/g, '');
const buildFlatSlotsFromOutputAnnotations = (annotations: OutputAnnotation[]): FlatSlot[] => {
  const flatSlots: FlatSlot[] = [];
  const labelCountMap: {
    [labelName: string]: number;
  } = {};
  for (const annotation of annotations) {
    if (annotation.annotationType !== 'Transcription') {
      continue;
    }
    if (!labelCountMap[annotation.annotationLabel.title]) {
      labelCountMap[annotation.annotationLabel.title] = 1;
    }
    const counter = labelCountMap[annotation.annotationLabel.title];
    labelCountMap[annotation.annotationLabel.title] = (counter ?? 0) + 1;
    const slotLabel = getLabelDisplayName(annotation.annotationLabel, counter ?? 0);
    const isSpeechLabel = annotation.annotationLabel.type === ANNOTATION_LABEL_TYPE.SPEECH_LABEL;
    annotation.data.forEach((slot, slotIndex) => {
      flatSlots.push({
        slotId: slot.id,
        annotationId: annotation.id,
        slotLabel,
        slotIndex,
        slot,
        color: PLACEHOLDER_COLOR,
        isSpeechLabel
      });
    });
  }
  flatSlots.sort((a, b) => a.slot.mediaTimestamp.start - b.slot.mediaTimestamp.start);
  return flatSlots;
};

/**
 * Plain text for WER: overlap tokens never include bracket tags (inspect UI may store
 * &lt;overlapping&gt; on {@link StructuredText.openingTag}/{@link StructuredText.closingTag}).
 * NSE and other non-overlap inline tags keep their markup.
 */
const structuredTextToWerFragment = (word: StructuredText): string => {
  if (word.type === INLINE_TAG_TYPE.SPEAKER_OVERLAP) {
    return word.transcript;
  }
  const core = word.transcript;
  if (word.openingTag || word.closingTag) {
    return `${word.openingTag ?? ''}${core}${word.closingTag ?? ''}`;
  }
  return core;
};

/**
 * Produces a single flattened transcript string for downstream WER (e.g. `[speaker1] hello ... | [speaker2] ...`).
 * Uses {@link getDeduplicatedGlobalTextForWer} (same deduplication as the global flattened view, with
 * overlap spans attributed to the source speaker for bracket grouping), strips overlap
 * duplication, omits overlap bracket tags from the string, and preserves other NSE / inline tag markup.
 *
 * Segments are split **per slot** (each time-bounded segment gets its own `[speakerN] ...` run), including
 * when the same speaker has multiple slots — e.g. `[speaker1] ... | [speaker1] ...` after another speaker
 * or after a slot that emitted no words.
 *
 * Returns `null` only when {@link isInspectSlotEnabled} is `false` or there are no annotations.
 * When inspect slot is enabled, the function always produces a transcript: if `flattenWords` are
 * available it uses the deduplication pipeline, otherwise it falls back to each slot's raw `transcript`.
 */
export const buildFlattenedTranscriptionTextForWer = (annotations: OutputAnnotation[], isInspectSlotEnabled: boolean): string | null => {
  if (!isInspectSlotEnabled) {
    return null;
  }
  const flatSlots = buildFlatSlotsFromOutputAnnotations(annotations);
  if (flatSlots.length === 0) {
    return null;
  }
  const annotationIdToLabel = new Map<string, string>();
  for (const fs of flatSlots) {
    if (!annotationIdToLabel.has(fs.annotationId)) {
      annotationIdToLabel.set(fs.annotationId, fs.slotLabel);
    }
  }
  const hasFlattenWords = flatSlots.some(fs => (fs.slot.flattenWords?.length ?? 0) > 0);
  if (hasFlattenWords) {
    return buildFromFlattenWords(flatSlots, annotationIdToLabel);
  }
  return buildFromSlotTranscripts(flatSlots);
};
const buildFromFlattenWords = (flatSlots: FlatSlot[], annotationIdToLabel: Map<string, string>): string => {
  const words = getDeduplicatedGlobalTextForWer(flatSlots);
  if (words.length === 0) {
    return '';
  }
  type Run = {
    parts: string[];
    normalizedTag: string;
    sourceSlotId: string;
  };
  const runs: Run[] = [];
  for (const word of words) {
    const fragment = structuredTextToWerFragment(word);
    const label = annotationIdToLabel.get(word.annotationId) ?? 'unknown';
    const normalizedTag = normalizeSpeakerTagForWer(label);
    const last = runs[runs.length - 1];
    // {@link getDeduplicatedGlobalTextForWer} sets `slotId` to the emitting slot; group by that so each
    // slot is its own run (same speaker + multiple slots ⇒ multiple `[speakerN]` segments).
    if (last && last.sourceSlotId === word.slotId) {
      last.parts.push(fragment);
    } else {
      runs.push({
        parts: [fragment],
        normalizedTag,
        sourceSlotId: word.slotId
      });
    }
  }
  return runs.map(run => `[${run.normalizedTag}] ${run.parts.join(' ')}`).join(' | ');
};
const buildFromSlotTranscripts = (flatSlots: FlatSlot[]): string => {
  const runs: {
    normalizedTag: string;
    text: string;
  }[] = [];
  for (const fs of flatSlots) {
    const trimmed = fs.slot.transcript.trim();
    if (!trimmed) {
      continue;
    }
    runs.push({
      normalizedTag: normalizeSpeakerTagForWer(fs.slotLabel),
      text: trimmed
    });
  }
  if (runs.length === 0) {
    return '';
  }
  return runs.map(run => `[${run.normalizedTag}] ${run.text}`).join(' | ');
};